import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Pagination, { usePagination } from "@/components/Pagination";

const EMPTY = { name: "", tds_rate_pct: 2, active: true };

export default function Parties() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/parties");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const {
    pageItems: pagedRows,
    page: pPage, setPage: setPPage,
    pageSize: pPageSize, setPageSize: setPPageSize,
    totalPages: pTotalPages, totalItems: pTotalItems,
  } = usePagination(rows, "voltledger.parties.pageSize");

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, tds_rate_pct: p.tds_rate_pct ?? 2, active: !!p.active });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.name.trim()) return toast.error("Party name is required");
      const payload = { ...form, tds_rate_pct: Number(form.tds_rate_pct || 0) };
      if (editing) {
        await api.put(`/parties/${editing.id}`, payload);
        toast.success("Party updated");
      } else {
        await api.post("/parties", payload);
        toast.success("Party added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete party "${p.name}"?`)) return;
    try {
      await api.delete(`/parties/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const canManage = hasPerm("parties", "edit") || hasPerm("parties", "create");
  const canDelete = hasPerm("parties", "delete");

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1100px]">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/90">Master Data</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Parties &amp; TDS Rates</h1>
          <p className="text-sm text-slate-500 mt-2">
            Maintain TDS withholding rate per Party/Client. Used to auto-compute TDS on new invoices for that party.
            Default is 2% when no rate is set.
          </p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none h-10" data-testid="add-party-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Party
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? "Edit Party" : "Add Party"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-1">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Party / Client Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="party-name" placeholder="e.g. TPNODL" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">TDS Rate (%)</Label>
                  <Input type="number" step="0.01" min="0" max="100" value={form.tds_rate_pct} onChange={(e) => setForm({ ...form, tds_rate_pct: e.target.value })} className="rounded-none mt-1 font-mono-data" data-testid="party-tds" />
                  <div className="text-[10px] text-slate-400 mt-1">Applied as % of Base Amount on invoices belonging to this party (unless overridden on the invoice).</div>
                </div>
                <div className="flex items-center justify-between border border-slate-200 px-3 py-2">
                  <Label className="text-sm">Active</Label>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="party-active" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submit} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-party">
                  {editing ? "Update" : "Add Party"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="card-surface rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Party / Client</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">TDS Rate</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="text-center text-sm text-slate-400 py-12">
                <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No parties defined. Default TDS of 2% applies to all invoices.
              </td></tr>
            )}
            {pagedRows.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`party-row-${p.name}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3 text-right font-mono-data text-slate-900 font-semibold">{Number(p.tds_rate_pct || 0).toFixed(2)} %</td>
                <td className="px-4 py-3">
                  <span className={`status-pill ${p.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {p.active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canManage && (
                    <button onClick={() => startEdit(p)} className="text-slate-600 hover:text-[#002FA7] inline-flex items-center gap-1 text-xs font-semibold mr-3" data-testid={`edit-party-${p.name}`}>
                      <Edit3 className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => del(p)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`del-party-${p.name}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={pPage} setPage={setPPage}
          pageSize={pPageSize} setPageSize={setPPageSize}
          totalPages={pTotalPages} totalItems={pTotalItems}
          testId="parties-pagination"
        />
      </div>
    </div>
  );
}
